import { useState } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { METRICS } from "@/lib/metricsGlossary";

// Small "i" icon: hover shows a one-line quick tip, click opens the full
// definition / why-it-matters / how-to-improve breakdown for a metric.
export const MetricInfo = ({ metricKey, className = "", side = "top", align = "start" }) => {
    const [popOpen, setPopOpen] = useState(false);
    const [tipOpen, setTipOpen] = useState(false);
    const m = METRICS[metricKey];
    if (!m) return null;

    return (
        <Popover open={popOpen} onOpenChange={setPopOpen}>
            <Tooltip open={tipOpen && !popOpen} onOpenChange={(v) => !popOpen && setTipOpen(v)}>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            data-testid={`metric-info-${metricKey}`}
                            aria-label={`What is ${m.label}?`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPopOpen((v) => !v); }}
                            className={`inline-flex shrink-0 items-center justify-center text-muted-foreground/50 hover:text-primary transition-colors ${className}`}
                        >
                            <Info className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side={side} className="max-w-[220px] text-xs z-[60]">
                    {m.quickTip}
                </TooltipContent>
            </Tooltip>
            <PopoverContent
                align={align}
                side={side}
                data-testid={`metric-popover-${metricKey}`}
                className="w-80 bg-popover border-border z-[60]"
            >
                <div className="space-y-3">
                    <div className="font-display text-sm text-foreground">{m.label}</div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{m.definition}</p>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Why it matters</div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{m.whyItMatters}</p>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">How to improve it</div>
                        <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                            {m.howToImprove.map((tip, i) => <li key={i}>{tip}</li>)}
                        </ul>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

// Convenience wrapper: label text + info icon inline, for headers/KPI labels.
export const MetricLabel = ({ metricKey, children, className = "", iconClassName = "" }) => (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
        {children}
        <MetricInfo metricKey={metricKey} className={iconClassName} />
    </span>
);
